import { useState, useCallback, useMemo } from 'react';

export interface UseCheckboxGroupOptions<T extends string> {
  items: T[];
  initialValues?: T[];
  onChange?: (checkedItems: T[], allChecked: boolean, someChecked: boolean) => void;
}

export interface UseCheckboxGroupReturn<T extends string> {
  checkedItems: Set<T>;
  allChecked: boolean;
  someChecked: boolean;
  indeterminate: boolean;
  toggleItem: (item: T) => void;
  toggleAll: (checked?: boolean) => void;
  setItem: (item: T, checked: boolean) => void;
  setAll: (items: T[]) => void;
  reset: () => void;
}

/**
 * Custom hook for managing parent-child checkbox relationships
 * Automatically handles indeterminate states and visual indicators
 * 
 * @example
 * ```tsx
 * const { checkedItems, allChecked, indeterminate, toggleItem, toggleAll } = useCheckboxGroup({
 *   items: ['item1', 'item2', 'item3'],
 *   initialValues: ['item1'],
 *   onChange: (checked, allChecked, someChecked) => {
 *     console.log('Checkbox state changed:', { checked, allChecked, someChecked });
 *   }
 * });
 * 
 * return (
 *   <div>
 *     <Checkbox 
 *       checked={allChecked}
 *       indeterminate={indeterminate}
 *       onCheckedChange={toggleAll}
 *     />
 *     {items.map(item => (
 *       <Checkbox 
 *         key={item}
 *         checked={checkedItems.has(item)}
 *         onCheckedChange={() => toggleItem(item)}
 *       />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useCheckboxGroup<T extends string>({
  items,
  initialValues = [],
  onChange,
}: UseCheckboxGroupOptions<T>): UseCheckboxGroupReturn<T> {
  const [checkedItems, setCheckedItems] = useState<Set<T>>(new Set(initialValues));

  // Calculate derived states
  const allChecked = useMemo(() => items.length > 0 && items.every(item => checkedItems.has(item)), [items, checkedItems]);
  const someChecked = useMemo(() => items.some(item => checkedItems.has(item)), [items, checkedItems]);
  const indeterminate = useMemo(() => someChecked && !allChecked, [someChecked, allChecked]);

  // Toggle individual item
  const toggleItem = useCallback((item: T) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(item)) {
        newSet.delete(item);
      } else {
        newSet.add(item);
      }
      return newSet;
    });
  }, []);

  // Toggle all items
  const toggleAll = useCallback((checked?: boolean) => {
    const newChecked = checked ?? !allChecked;
    setCheckedItems(newChecked ? new Set(items) : new Set());
  }, [allChecked, items]);

  // Set specific item state
  const setItem = useCallback((item: T, checked: boolean) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(item);
      } else {
        newSet.delete(item);
      }
      return newSet;
    });
  }, []);

  // Set all items to specific state
  const setAll = useCallback((itemsToSet: T[]) => {
    setCheckedItems(new Set(itemsToSet));
  }, []);

  // Reset to initial state
  const reset = useCallback(() => {
    setCheckedItems(new Set(initialValues));
  }, [initialValues]);

  // Call onChange when state changes
  const checkedItemsArray = useMemo(() => Array.from(checkedItems), [checkedItems]);
  
  // Notify parent of changes
  if (onChange) {
    // Use setTimeout to avoid calling onChange during render
    setTimeout(() => {
      onChange(checkedItemsArray, allChecked, someChecked);
    }, 0);
  }

  return {
    checkedItems,
    allChecked,
    someChecked,
    indeterminate,
    toggleItem,
    toggleAll,
    setItem,
    setAll,
    reset,
  };
}
